/**
 * Firmware constant mirrors.
 *
 * Every value here is transcribed from the Realtek Ameba Wi-Fi API headers so the
 * backend can decode a raw measurement report without the device having to send
 * human-readable strings (keeping the on-wire payload small for an MCU).
 *
 * Source of truth:
 *   wifi/component/wifi/api/wifi_api_types.h  (struct rtw_scan_result, enums)
 *   wifi/component/wifi/api/wifi_api.h        (wifi_scan_networks / wifi_get_scan_records)
 */
/** `enum rtw_supported_band` — wifi_api_types.h:232 */
export const RTW_BAND = {
    RTW_BAND_ON_24G: 0,
    RTW_BAND_ON_5G: 1,
    RTW_BAND_ON_6G: 2,
};
export function bandToGhz(band) {
    switch (band) {
        case RTW_BAND.RTW_BAND_ON_24G:
            return 2.4;
        case RTW_BAND.RTW_BAND_ON_5G:
            return 5;
        case RTW_BAND.RTW_BAND_ON_6G:
            return 6;
        default:
            return null;
    }
}
/**
 * `enum rtw_wireless_mode` — wifi_api_types.h:595. Bit flags.
 */
export const RTW_80211 = {
    RTW_80211_INVALID: 0,
    RTW_80211_B: 1 << 0,
    RTW_80211_A: 1 << 1,
    RTW_80211_G: 1 << 2,
    RTW_80211_N: 1 << 3,
    RTW_80211_AC: 1 << 4,
    RTW_80211_AX: 1 << 5,
};
/** Mirrors the precedence used by the firmware's own `print_scan_result()` in atcmd_wifi.c. */
export function wirelessModeToString(mode) {
    if (mode & RTW_80211.RTW_80211_AX)
        return 'AX';
    if (mode & RTW_80211.RTW_80211_AC)
        return 'AC';
    if (mode & RTW_80211.RTW_80211_N)
        return 'N';
    if (mode & RTW_80211.RTW_80211_A)
        return 'A';
    if (mode & RTW_80211.RTW_80211_G)
        return 'G';
    if (mode & RTW_80211.RTW_80211_B)
        return 'B';
    return 'Unknown';
}
/**
 * `enum rtw_security_flag` — wifi_api_types.h:121. Bit flags composing the u32
 * `rtw_scan_result.security`. The firmware sends the raw value; we decode it here.
 */
export const RTW_SECURITY_BITS = {
    WEP_ENABLED: 1 << 0,
    TKIP_ENABLED: 1 << 1,
    AES_ENABLED: 1 << 2,
    AES_CMAC_ENABLED: 1 << 4,
    ENTERPRISE_ENABLED: 1 << 5,
    OWE_ENABLED: 1 << 6,
    SHARED_ENABLED: 1 << 15,
    WPA_SECURITY: 1 << 21,
    WPA2_SECURITY: 1 << 22,
    WPA3_SECURITY: 1 << 23,
};
/** `RTW_SECURITY_OPEN` — wifi_api_types.h:258 */
export const RTW_SECURITY_OPEN = 0;
export function securityToString(security) {
    if (security === RTW_SECURITY_OPEN)
        return 'Open';
    const protocols = [];
    if (security & RTW_SECURITY_BITS.WPA3_SECURITY)
        protocols.push('WPA3');
    if (security & RTW_SECURITY_BITS.WPA2_SECURITY)
        protocols.push('WPA2');
    if (security & RTW_SECURITY_BITS.WPA_SECURITY)
        protocols.push('WPA');
    if (security & RTW_SECURITY_BITS.WEP_ENABLED)
        protocols.push('WEP');
    const ciphers = [];
    if (security & RTW_SECURITY_BITS.AES_ENABLED)
        ciphers.push('AES');
    if (security & RTW_SECURITY_BITS.TKIP_ENABLED)
        ciphers.push('TKIP');
    const suffix = [];
    if (security & RTW_SECURITY_BITS.ENTERPRISE_ENABLED)
        suffix.push('Enterprise');
    if (security & RTW_SECURITY_BITS.OWE_ENABLED)
        suffix.push('OWE');
    if (protocols.length === 0)
        return `Unknown(0x${security.toString(16)})`;
    return [protocols.join('/'), ciphers.join('+'), suffix.join(' ')].filter(Boolean).join(' ');
}
/** Maximum SSID length — `RTW_ESSID_MAX_SIZE`, wifi_api_types.h:70 */
export const RTW_ESSID_MAX_SIZE = 32;
/**
 * 802.11k RCPI → dBm.
 *
 * RCPI is encoded in 0.5 dBm steps starting at -110 dBm (IEEE 802.11-2020 §9.4.2.38),
 * so dBm = (RCPI / 2) - 110. Values 0..220 are valid; 255 means "unavailable".
 * Normalising here keeps beacon-report fingerprints comparable with plain scan RSSI.
 */
export function rcpiToDbm(rcpi) {
    if (!Number.isFinite(rcpi) || rcpi < 0 || rcpi > 220)
        return null;
    return rcpi / 2 - 110;
}
export function dbmToRcpi(dbm) {
    return Math.max(0, Math.min(220, Math.round((dbm + 110) * 2)));
}
/**
 * 802.11k RSNI → dB.
 * RSNI is encoded in 0.5 dB steps starting at -10 dB: dB = (RSNI / 2) - 10.
 */
export function rsniToDb(rsni) {
    if (!Number.isFinite(rsni) || rsni < 0 || rsni > 255)
        return null;
    return rsni / 2 - 10;
}
/** Channel number → band, used when the device omits the `band` field. */
export function channelToBand(channel) {
    if (channel >= 1 && channel <= 14)
        return RTW_BAND.RTW_BAND_ON_24G;
    if (channel >= 36 && channel <= 177)
        return RTW_BAND.RTW_BAND_ON_5G;
    return RTW_BAND.RTW_BAND_ON_6G;
}
//# sourceMappingURL=firmware.js.map